import { useState } from "react";
import {
  ArrowTrending16Regular,
  Diamond16Regular,
  ErrorCircle16Regular,
  Info16Regular,
  ShieldError16Regular,
  Sparkle16Regular,
  Warning16Regular,
} from "@fluentui/react-icons";
import { AppDialog } from "@/components/app-dialog";
import { CompactKpi } from "@/components/compact-kpi";
import { DetailSection } from "@/components/detail-section";
import { FuiButton } from "@/components/fui-control";
import { SERIES } from "@/components/visual/chart-colors";
import { StackedBar } from "@/components/visual/charts";
import { ENGINE_VERSION } from "@/engine/cpm";
import { toDay } from "@/engine/date";
import type { HealthIssue } from "@/engine/health";
import { KV } from "@/features/inspector/Inspector";
import { formatDate } from "@/lib/date-format";
import { displayBaselineName, fmtDelta, fmtSpi } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useProjectStore } from "@/store/project-store";

export function InsightsPanel() {
  const project = useProjectStore((s) => s.project);
  const derived = useProjectStore((s) => s.derived);
  const lastImpact = useProjectStore((s) => s.lastImpact);
  const { schedule, health, evm, delays, baseline, calendar } = derived;
  const select = useProjectStore.getState().select;
  const [engineOpen, setEngineOpen] = useState(false);

  const leaves = schedule.order.filter((id) => !schedule.tasks.get(id)!.isSummary);
  const done = leaves.filter((id) => schedule.tasks.get(id)!.percentComplete >= 100).length;
  const inProgress = leaves.filter((id) => {
    const p = schedule.tasks.get(id)!.percentComplete;
    return p > 0 && p < 100;
  }).length;
  const statusDay = toDay(project.statusDate);
  const upcoming = schedule.order
    .map((id) => schedule.tasks.get(id)!)
    .filter((t) => t.isMilestone && toDay(t.start) >= statusDay && toDay(t.start) - statusDay <= 14)
    .slice(0, 5);
  const overallPct = leaves.length
    ? Math.round(
        leaves.reduce((a, id) => a + schedule.tasks.get(id)!.percentComplete * Math.max(schedule.tasks.get(id)!.duration, 0.5), 0) /
          leaves.reduce((a, id) => a + Math.max(schedule.tasks.get(id)!.duration, 0.5), 0),
      )
    : 0;
  const notStarted = leaves.length - done - inProgress;
  const maxVariance = delays.reduce((m, d) => Math.max(m, d.varianceDays), 1);

  const finishTone = !baseline
    ? undefined
    : evm.finishVarianceDays > 0
      ? "danger"
      : evm.finishVarianceDays < 0
        ? "brand"
        : undefined;

  const progressSegments = [
    { key: "done" as const, label: "완료", value: done, color: SERIES.good },
    { key: "inProgress" as const, label: "진행", value: inProgress, color: SERIES.actual },
    { key: "notStarted" as const, label: "미착수", value: notStarted, color: SERIES.empty },
  ];

  return (
    <div className="thin-scrollbar h-full overflow-auto p-3 space-y-3">
      {/* headline & project finish */}
      <DetailSection title="종료일" variant="card">
        <div className="space-y-3">
          <CompactKpi
            label="프로젝트 종료일"
            value={formatDate(schedule.projectFinish, "numeric")}
            hint={
              baseline
                ? `${displayBaselineName(baseline.name)} 대비 ${fmtDelta(evm.finishVarianceDays)}`
                : undefined
            }
            unit={`총 ${schedule.projectDuration}일`}
            tone={finishTone}
          />
          <div className="text-body text-foreground">
            {schedule.criticalPath.length > 0
              ? <>여유 없는 작업 <span className="tabular">{schedule.criticalPath.length}</span>개가 종료일을 결정합니다</>
              : "여유 없는 작업이 없습니다"}
          </div>
          <div className="text-body text-muted-foreground">
            {formatDate(schedule.projectStart, "numeric")} 시작 · 전체 가중 진척 <span className="font-normal tabular text-foreground">{overallPct}%</span>
          </div>
          <StackedBar
            segments={progressSegments}
            ariaLabel={`공정 진척: 전체 ${overallPct}%, 완료 ${done}, 진행 ${inProgress}, 미착수 ${notStarted}`}
          />
        </div>
      </DetailSection>

      {/* impact of last change */}
      {lastImpact && (
        <div className="rounded-lg border border-tint/30 bg-tint/8 p-3 text-body">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 font-normal text-foreground">
              <Sparkle16Regular className="size-4 text-tint" />
              <span>방금 바꾼 것의 영향</span>
            </div>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-caption font-normal tabular",
                lastImpact.finishDeltaDays > 0
                  ? "bg-critical/15 text-critical font-medium"
                  : lastImpact.finishDeltaDays < 0
                    ? "bg-tint/15 text-tint font-medium"
                    : "bg-muted text-muted-foreground",
              )}
            >
              {fmtDelta(lastImpact.finishDeltaDays)}
            </span>
          </div>
          <div className="mt-2 font-normal text-foreground">{lastImpact.label}</div>
          <div className="mt-0.5 text-caption text-muted-foreground">
            종료일 {formatDate(lastImpact.previousFinish, "short")} → {formatDate(lastImpact.finish, "short")}
          </div>
          <ul className="mt-2 space-y-1 text-body text-foreground/90">
            <li>
              영향받은 작업{" "}
              <button
                type="button"
                className="font-normal text-tint underline hover:text-tint/80"
                onClick={() => select(lastImpact.changedTaskIds, lastImpact.changedTaskIds[0] ?? null)}
              >
                {lastImpact.changedTaskIds.length}개
              </button>
            </li>
            {lastImpact.criticalPathChanged && (
              <li>여유 없는 작업이 달라졌습니다 (새로 {lastImpact.newlyCritical.length}개 · 해소 {lastImpact.noLongerCritical.length}개)</li>
            )}
            {lastImpact.newNegativeFloat.length > 0 && (
              <li className="text-critical font-normal">{lastImpact.newNegativeFloat.length}개 작업이 마감을 지킬 수 없게 됐습니다</li>
            )}
            {lastImpact.finishDeltaDays === 0 && lastImpact.changedTaskIds.length > 0 && (
              <li>종료일은 그대로입니다. 여유가 있어 후속 작업이 밀리지 않았습니다.</li>
            )}
          </ul>
        </div>
      )}

      {/* EVM */}
      <DetailSection
        title="계획 대비 진행률 (SPI)"
        icon={<ArrowTrending16Regular className="size-4 text-muted-foreground" />}
        variant="card"
      >
        {evm.hasBaseline ? (
          <div className="space-y-2.5">
            <CompactKpi
              label="일정 성과 지수 (SPI)"
              value={fmtSpi(evm.spi)}
              hint={`${formatDate(project.statusDate, "short")} 기준`}
              unit={`계획 ${Math.round(evm.pv * 100)}% · 실제 ${Math.round(evm.ev * 100)}%`}
              tone={
                evm.spi === null
                  ? "muted"
                  : evm.spi < 0.9
                    ? "danger"
                    : evm.spi < 1.0
                      ? "warning"
                      : "brand"
              }
            />
            <SCurve curve={evm.curve} statusDay={evm.statusDay} ev={evm.ev} pv={evm.pv} />
            <p className="text-body leading-relaxed text-muted-foreground">
              {evm.spi === null
                ? "진척 기준일까지 계획된 일이 아직 없습니다."
                : evm.spi < 0.9
                  ? "계획보다 많이 늦어졌습니다. 아래 가장 많이 늦어진 작업부터 확인해 주세요."
                  : evm.spi < 1
                    ? "계획보다 조금 늦어졌습니다. 여유 없는 작업의 진행을 확인해 주세요."
                    : "계획대로 또는 앞서 가고 있습니다. 단, 중요하지 않은 작업만 앞선 것일 수도 있으니 여유 없는 작업도 확인해 주세요."}
            </p>
          </div>
        ) : (
          <p className="text-body leading-relaxed text-muted-foreground">
            처음 계획(기준선)이 없어 지연 여부를 계산할 수 없습니다. 계획이 확정되면 툴바의 “기준선 › 현재 일정을 기준선으로 저장”을 눌러 주세요.
          </p>
        )}
      </DetailSection>

      {/* top delay */}
      {evm.hasBaseline && (
        <DetailSection
          title="가장 많이 늦어진 작업"
          icon={<ErrorCircle16Regular className="size-4 text-muted-foreground" />}
          hint={delays.length ? `${delays.length}개` : undefined}
          variant="card"
        >
          {delays.length === 0 ? (
            <p className="text-body text-muted-foreground">처음 계획보다 늦어진 작업이 없습니다.</p>
          ) : (
            <ul className="space-y-1">
              {delays.map((d) => (
                <li key={d.taskId}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-foreground/5"
                    onClick={() => select([d.taskId], d.taskId)}
                  >
                    <span className="w-10 shrink-0 font-mono text-body text-muted-foreground tabular">{d.wbs}</span>
                    <span className="min-w-0 flex-1 truncate text-body font-normal text-foreground">{d.name}</span>
                    <span className="relative h-1 w-14 shrink-0 rounded-full bg-muted" aria-hidden>
                      <span
                        className={cn("absolute inset-y-0 left-0 rounded-full", d.critical ? "bg-critical" : "bg-foreground/70")}
                        style={{ width: `${(d.varianceDays / maxVariance) * 100}%`, minWidth: 2 }}
                      />
                    </span>
                    <span className={cn("w-9 shrink-0 text-right text-body font-normal tabular", d.critical ? "text-critical" : "text-foreground")}>
                      +{d.varianceDays}d
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DetailSection>
      )}

      {/* health */}
      <DetailSection
        title="일정 건강도"
        icon={<ShieldError16Regular className="size-4 text-muted-foreground" />}
        hint={health.issues.length ? `${health.issues.length}건` : undefined}
        action={
          <span
            className={cn(
              "rounded-md px-1.5 py-0.5 text-caption font-normal tabular",
              health.score < 60
                ? "bg-critical/15 text-critical font-medium"
                : health.score < 80
                  ? "bg-warning/15 text-warning-foreground font-medium"
                  : "bg-muted text-foreground",
            )}
          >
            {health.score}점
          </span>
        }
        variant="card"
      >
        {health.issues.length === 0 ? (
          <p className="text-body text-muted-foreground">발견된 문제가 없습니다. 좋습니다.</p>
        ) : (
          <ul className="space-y-1">
            {health.issues.map((i) => (
              <IssueRow key={i.code} issue={i} onSelect={() => i.taskIds.length && select(i.taskIds, i.taskIds[0]!)} />
            ))}
          </ul>
        )}
      </DetailSection>

      {/* upcoming milestones */}
      <DetailSection
        title="다음 2주 마일스톤"
        icon={<Diamond16Regular className="size-4 text-muted-foreground" />}
        hint={upcoming.length ? `${upcoming.length}개` : undefined}
        variant="card"
      >
        {upcoming.length === 0 ? (
          <p className="text-body text-muted-foreground">앞으로 2주 안에 마일스톤이 없습니다.</p>
        ) : (
          <ul className="space-y-1">
            {upcoming.map((t) => {
              const task = project.tasks.find((x) => x.id === t.id)!;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-foreground/5"
                    onClick={() => select([t.id], t.id)}
                  >
                    <span className="min-w-0 flex-1 truncate text-body font-normal text-foreground">{task.name}</span>
                    <span className="text-body font-normal text-muted-foreground tabular">{formatDate(t.start, "short")}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DetailSection>

      {/* engine info */}
      <DetailSection
        title="계산 정보"
        icon={<Info16Regular className="size-4 text-muted-foreground" />}
        collapsible
        defaultOpen={false}
        variant="card"
      >
        <KV
          label="엔진"
          value={
            <button type="button" className="text-tint underline hover:text-tint/80" onClick={() => setEngineOpen(true)}>
              {schedule.engineVersion}
            </button>
          }
        />
        <KV label="캘린더" value={`주 ${calendar.workingDaysPerWeek}일 · 휴일 ${project.calendar.holidays.length}`} />
        <KV label="크리티컬 기준" value={`여유 ≤ ${project.criticalThreshold}d`} />
      </DetailSection>
      <EngineAboutDialog open={engineOpen} onClose={() => setEngineOpen(false)} />
    </div>
  );
}

function EngineAboutDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={`일정 엔진 ${ENGINE_VERSION}`}
      description="TypeScript로 구현한 CPM 스케줄러입니다. 종료일·여유·SPI는 이 엔진이 계산합니다."
      width="md"
      footer={
        <FuiButton density="dialog" appearance="primary" onClick={onClose}>
          닫기
        </FuiButton>
      }
    >
      <dl className="space-y-2 text-body">
        <div>
          <dt className="font-normal text-foreground">무엇을 계산하나</dt>
          <dd className="mt-0.5 text-muted-foreground">작업 기간, 선행 관계, 제약, 근무 캘린더를 넣어 각 작업의 시작·종료와 여유를 정합니다. 여유가 0이하면 여유 없는 작업으로 표시합니다.</dd>
        </div>
        <div>
          <dt className="font-normal text-foreground">어디서 도나</dt>
          <dd className="mt-0.5 text-muted-foreground">지금 빌드는 브라우저에서 바로 계산합니다. 같은 입력이면 같은 일정이 나옵니다.</dd>
        </div>
        <div>
          <dt className="font-normal text-foreground">버전을 왜 남기나</dt>
          <dd className="mt-0.5 text-muted-foreground">
            <code className="rounded-sm bg-muted px-1 py-px text-caption">{ENGINE_VERSION}</code>는 계산기 이름과 버전입니다. 나중에 서버나 다른 엔진이 생기면, 어떤 버전으로 계산했는지 구분하기 위해 결과에 기록합니다.
          </dd>
        </div>
      </dl>
    </AppDialog>
  );
}

function IssueRow({ issue, onSelect }: { issue: HealthIssue; onSelect: () => void }) {
  const Icon = issue.severity === "error" ? ErrorCircle16Regular : issue.severity === "warning" ? Warning16Regular : Info16Regular;
  return (
    <li>
      <button
        type="button"
        className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-foreground/5"
        onClick={onSelect}
        disabled={!issue.taskIds.length}
      >
        <Icon
          className={cn(
            "mt-[2px] size-3.5 shrink-0",
            issue.severity === "error"
              ? "text-critical"
              : issue.severity === "warning"
                ? "text-warning-foreground"
                : "text-muted-foreground",
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between text-body">
            <span className="font-normal text-foreground">{issue.title}</span>
            {issue.taskIds.length > 0 && <span className="font-normal text-muted-foreground tabular">{issue.taskIds.length}</span>}
          </div>
          <div className="mt-0.5 text-body text-muted-foreground">{issue.detail}</div>
        </div>
      </button>
    </li>
  );
}

function SCurve({ curve, statusDay, ev, pv }: { curve: Array<{ day: number; pv: number }>; statusDay: number; ev: number; pv: number }) {
  if (curve.length < 2) return null;
  const w = 240;
  const h = 56;
  const maxDay = curve[curve.length - 1]!.day || 1;
  const x = (d: number) => (d / maxDay) * w;
  const y = (v: number) => h - v * (h - 8) - 4;
  const path = curve.map((p, i) => `${i ? "L" : "M"}${x(p.day).toFixed(1)},${y(p.pv).toFixed(1)}`).join(" ");
  const sx = x(Math.min(statusDay, maxDay));
  return (
    <div className="mt-2">
      <svg viewBox={`0 0 ${w} ${h}`} className="h-14 w-full" preserveAspectRatio="none">
        {/* Baseline planned curve */}
        <path d={path} fill="none" className="stroke-muted-foreground/60" strokeWidth={1.5} vectorEffect="non-scaling-stroke" strokeDasharray="3 2" />
        {/* Status date guide line */}
        <line x1={sx} x2={sx} y1={0} y2={h} className="stroke-tint/60" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        {/* PV dot */}
        <circle cx={sx} cy={y(pv)} r={3} className="fill-muted-foreground" />
        {/* EV dot */}
        <circle cx={sx} cy={y(ev)} r={3.5} className={ev < pv ? "fill-critical" : "fill-tint"} />
      </svg>
      <div className="mt-1 flex items-center justify-between text-caption text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-2 rounded-full bg-muted-foreground/70" />
          계획 {Math.round(pv * 100)}%
        </span>
        <span className="flex items-center gap-1.5">
          <span className={cn("inline-block size-2 rounded-full", ev < pv ? "bg-critical" : "bg-tint")} />
          실제 {Math.round(ev * 100)}%
        </span>
        <span className="tabular font-medium text-foreground">{ev >= pv ? "순항" : `${Math.round((pv - ev) * 100)}%p 지연`}</span>
      </div>
    </div>
  );
}
